import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Theme,
    Typography
} from "@mui/material";
import {
    SyntheticEvent,
    useEffect,
    useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReportsListRes } from "../../../redux-store/types/api-types";
import { GetCustomerPortfoliosResponseBody } from "../../../redux-store/types/analytics";
import { RootStateType } from "../../../redux-store/reducers";
import { getCustomerPortfolios } from "../../../redux-store/actions/analytics";
import { DropdownLoading } from "../loadingComponents";
import { maskInfo } from "../../../utils/utilityFunctions";
import { SearchIcon } from "../../customSVGs";
import Compliance from "./compliance";
import { getReportsInvestorList } from "../../../redux-store/actions/reports";
import DescriptiveAnalysis from "./descriptiveAnalysis";
import PrescriptiveAnalysis from "./prescriptiveAnalysis";
import { USER_ROLES } from "../../../utils/constant";

const portfolioAnalyticsTabs = [
    "Descriptive Analysis",
    "Diagnostic Analysis",
    "Prescriptive Analysis",
    "Predictive Analysis",
]

export default function PortfolioAnalytics(): JSX.Element {
    const dispatch = useDispatch();

    const { iwappRmId = "", investorClientId = "" } = useSelector((state: RootStateType) => state.id);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);

    const [investorListLoading, setInvestorListLoading] = useState<boolean>(false);
    const [selectedInvestor, setSelectedInvestor] = useState<string | null>([USER_ROLES.INVESTOR].includes(role) ? investorClientId : null);
    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);

    const [portfolioListLoading, setPortfolioListLoading] = useState<boolean>(false);
    const [selectedPortfolio, setSelectedPortfolio] = useState<string>("");
    const [portfolioListData, setPortfolioListData] = useState<GetCustomerPortfoliosResponseBody>({} as GetCustomerPortfoliosResponseBody);

    const [activePortfolioAnalyticsTab, setActivePortfolioAnalyticsTab] = useState<string>("Descriptive Analysis")

    const fetchInvestorList = async () => {
        try {
            setInvestorListLoading(true);

            const payload = {
                rmId: `${iwappRmId}`
            }

            const response = await (dispatch(getReportsInvestorList(payload)) as unknown as Promise<ReportsListRes>);

            setInvestorListData(response);
        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setInvestorListLoading(false);
        }
    };

    const handleInvestorChange = async (event: SyntheticEvent | null, newValue: string | null) => {
        try {
            setPortfolioListLoading(true)
            setSelectedPortfolio("")
            setSelectedInvestor(newValue);

            if ((newValue ?? "")?.length) {

                const payload = {
                    rmId: [USER_ROLES.INVESTOR].includes(role) ? `${investorRmId}` : `${iwappRmId}`,
                    clientCode: [USER_ROLES.INVESTOR].includes(role) ? `${newValue}` : investorListData?.result?.find(obj => obj?.customerName?.toLowerCase() === newValue?.toLowerCase())?.clientCode ?? ""
                }

                const response = await (dispatch(getCustomerPortfolios(payload)) as unknown as Promise<GetCustomerPortfoliosResponseBody>);

                setPortfolioListData(response)

                if ((response?.portfolios ?? [])?.length) {
                    setSelectedPortfolio(response?.portfolios?.[0]?.portfolioId ?? "")
                }
            }
        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setPortfolioListLoading(false)
        }
    }

    useEffect(() => {
        if (![USER_ROLES.INVESTOR].includes(role)) {
            fetchInvestorList();
        }

        if ([USER_ROLES.INVESTOR].includes(role)) {
            handleInvestorChange(null, investorClientId)
        }
    }, [])

    const handlePortfolioAnalyticsTabChange = (activeTab: string) => {
        setActivePortfolioAnalyticsTab(activeTab)
    }

    const handlePortfolioChange = (event: SelectChangeEvent) => {
        setSelectedPortfolio(event.target.value);
    };

    const investorListOptions = investorListData?.result?.map(obj => obj?.customerName ?? "") ?? []
    const portfolioListOptions = portfolioListData?.portfolios?.map(obj => ({
        label: obj?.portfolioName ?? "",
        value: obj?.portfolioId ?? ""
    })) ?? []

    const selectedInvestorClientCode = investorListData?.result?.find(obj => obj?.customerName?.toLowerCase() === selectedInvestor?.toLowerCase())?.clientCode ?? ""

    return (
        <>
            {/* Page Heading */}
            <Box>
                <Typography
                    variant="h1"
                    sx={(theme: Theme) => ({
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary
                    })}
                >
                    Portfolio Analytics
                </Typography>
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    marginTop: "25px",
                    borderRadius: "25px",
                    padding: "35px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                {/* Search Investor Box */}
                <Typography
                    variant="h3"
                    sx={{ marginBottom: '20px' }}
                >
                    Investor
                </Typography>

                {investorListLoading && (<DropdownLoading />)}

                {!investorListLoading && (
                    <Autocomplete
                        // value={maskInfo(selectedInvestor ?? "")}
                        value={selectedInvestor}
                        onChange={handleInvestorChange}
                        options={investorListOptions}
                        disabled={
                            portfolioListLoading ||
                            [USER_ROLES.INVESTOR].includes(role)
                        }
                        sx={{
                            width: '100%',
                            borderRadius: '50px'
                        }}
                        renderInput={(params) =>
                            <TextField {...params} label=""
                                placeholder="Search Investor...."
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>),
                                    disableUnderline: true,
                                    sx: {
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderRadius: '8px',
                                        },
                                    },
                                }}
                            />
                        }
                    // renderOption={(props, option) => (
                    //     <li {...props}>
                    //         {maskInfo(option ?? "")}
                    //     </li>
                    // )}
                    />
                )}
                {/* Portfolio Dropdown */}
                {selectedInvestor && (
                    <Box sx={{ width: "25%", marginTop: "30px" }}>
                        {portfolioListLoading && (<DropdownLoading />)}

                        {!portfolioListLoading && (
                            <FormControl fullWidth>
                                <InputLabel id="portfolio-select">Portfolio</InputLabel>
                                <Select
                                    labelId="portfolio-select"
                                    value={selectedPortfolio}
                                    label="Portfolio"
                                    onChange={handlePortfolioChange}
                                    sx={{ borderRadius: '8px' }}
                                >
                                    {portfolioListOptions.map(obj => (
                                        <MenuItem
                                            key={obj.label}
                                            value={obj.value}
                                        >
                                            {maskInfo(obj?.label ?? "")}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                )}
            </Box>
            {/* Tabs container */}
            {(selectedInvestor && selectedPortfolio) && (
                <>
                    {/* Tabs */}
                    <Box key={selectedPortfolio}>
                        <Box
                            sx={(theme: Theme) => ({
                                display: 'flex',
                                gap: '10px',
                                paddingBottom: '15px',
                                borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                overflowX: 'auto',
                                marginTop: "30px"
                            })}
                        >
                            {portfolioAnalyticsTabs.map((item) => {
                                return (
                                    <Button
                                        variant={activePortfolioAnalyticsTab === item ? "contained" : "text"}
                                        key={item}
                                        onClick={() => handlePortfolioAnalyticsTabChange(item)}
                                        sx={(theme: Theme) => ({
                                            padding: '0px 15px',
                                            background: activePortfolioAnalyticsTab === item ? theme.palette.primary[500] : 'transparent',
                                            borderRadius: '7px',
                                            outline: 'none',
                                            border: 'none',
                                            cursor: 'pointer'
                                        })}
                                        disabled={item === "Predictive Analysis"}
                                    >
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                color: activePortfolioAnalyticsTab === item ? theme.palette.textDark.primary : theme.palette.text.secondary
                                            })}
                                        >
                                            {item}
                                        </Typography>
                                    </Button>
                                )
                            })}
                        </Box>
                        <Box sx={{ marginTop: "25px" }}>
                            {activePortfolioAnalyticsTab === "Descriptive Analysis" && (
                                <DescriptiveAnalysis
                                    key={selectedPortfolio}
                                    clientCode={selectedInvestorClientCode}
                                    portfolioId={selectedPortfolio}
                                />
                            )}
                            {activePortfolioAnalyticsTab === "Diagnostic Analysis" && (
                                <Compliance
                                    key={selectedPortfolio}
                                    clientCode={selectedInvestorClientCode}
                                    portfolioId={selectedPortfolio}
                                />
                            )}
                            {activePortfolioAnalyticsTab === "Prescriptive Analysis" && (
                                <PrescriptiveAnalysis key={selectedPortfolio}
                                />
                            )}

                        </Box>
                    </Box>
                </>
            )}
        </>
    )
}